import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { UseMutationOptions, useQueryClient } from 'react-query';
import type { ProductBrand } from './types';
import { productBrandsEndpoint } from './use-get-product-brands-api';

export interface UpdateProductBrandPayload {
  name?: string;
  isActive?: boolean;
}

type TData = ProductBrand;
type TError = AxiosError;
type TVariables = { id: number; data: UpdateProductBrandPayload };

type Options = Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>;
type TReturnType = ReturnType<typeof useMutationWrapper<TData, TError, TVariables>>;

// PUT ums/v1/cms/product-brands/:id
// The BE refuses a rename while the brand is used by live master products
// (409 with an explanatory message — surfaced by the error toast).
export function useUpdateProductBrandApi(options?: Options): TReturnType {
  const queryClient = useQueryClient();
  const toast = useCustomToast();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async ({ id, data }: TVariables): Promise<TData> => {
    try {
      const response = await http.put(`${productBrandsEndpoint}/${id}`, data);
      return response.data.data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [`${productBrandsEndpoint}/update`],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([productBrandsEndpoint]);
      queryClient.invalidateQueries([`${productBrandsEndpoint}/usage`]);
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
